import { isFunction } from '@agilepoint/utils-common';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { StackPanel } from '../stack-panel';
import { Star } from './parts/star';
import { IRatingProps } from './types';

function RatingBase(props: IRatingProps) {
    const {
        className, maxRating: maxStars = 5, step = '1', value, defaultValue = 0,
        backgroundColor, borderColor, size = 'medium', hidden, readonly, onChange
    } = props;
    const list = useMemo(() => Array(maxStars).fill(0), [maxStars]);
    const [selectedValue, setSelectedValue] = useState(value ?? defaultValue);
    const { rating: { composites: { gap } } } = useTheme();

    useLayoutEffect(() => {
        if (value === undefined) {
            return;
        }
        setSelectedValue(value);
    }, [value]);

    useLayoutEffect(() => {
        const stepNum = Number(step);
        if (selectedValue % stepNum !== 0) {
            setSelectedValue(Math.ceil(selectedValue / stepNum) * stepNum);
            return;
        }
        if (value !== undefined && value !== selectedValue && value % stepNum === 0) {
            setSelectedValue(value);
        }
    }, [selectedValue, step, value]);

    const _onChange = useCallback((selectedValue: number) => {
        if (readonly) {
            return;
        }
        if (value === undefined) {
            setSelectedValue(selectedValue);
        }
        if (isFunction(onChange)) {
            onChange(selectedValue);
        }
    }, [onChange, readonly, value]);

    return hidden ? null : (
        <StackPanel className={className} orientation="horizontal" gap={gap} inline>
            {
                list.map((v, idx) => (
                    <Star key={idx} readonly={readonly} size={size} step={step} index={idx} value={selectedValue}
                        backgroundColor={backgroundColor} borderColor={borderColor} onChange={_onChange} />
                ))
            }
        </StackPanel>
    );
}

export const Rating = styled(RatingBase).attrs({
    className: String('ap-starRating')
})`
    height: auto;
    width: auto;
`;
